import { apiService } from './api';
import { PaginationParams } from '../types/common';
import {
  Diet,
  Menu,
  Recipe,
  RecipePlate,
  RecipePlateProfile,
  Price,
  CreateDietRequest,
  UpdateDietRequest,
  CreateMenuRequest,
  UpdateMenuRequest,
  CreateRecipeRequest,
  UpdateRecipeRequest,
  CreateRecipePlateRequest,
  UpdateRecipePlateRequest,
  CreatePriceRequest,
  UpdatePriceRequest,
  DietsResponse,
  MenusResponse,
  RecipesResponse,
  RecipePlateProfilesResponse,
  RecipePlatesResponse,
  PricesResponse,
} from '../types/diet';

class DietService {
  // Diets
  async getDiets(params?: PaginationParams & {
    name?: string;
    code?: string;
    status?: string;
    startingDay?: string;
    startingDateFrom?: string;
    startingDateTo?: string;
  }): Promise<DietsResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.name) queryParams.append('name', params.name);
    if (params?.code) queryParams.append('code', params.code);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.startingDay) queryParams.append('startingDay', params.startingDay);
    if (params?.startingDateFrom) queryParams.append('startingDateFrom', params.startingDateFrom);
    if (params?.startingDateTo) queryParams.append('startingDateTo', params.startingDateTo);

    return apiService.get<DietsResponse>(`/diets?${queryParams.toString()}`);
  }

  async getDiet(id: string): Promise<{ diet: Diet }> {
    return apiService.get<{ diet: Diet }>(`/diets/${id}`);
  }

  async createDiet(data: CreateDietRequest): Promise<{ message: string; diet: Diet }> {
    return apiService.post<{ message: string; diet: Diet }>('/diets', data);
  }

  async updateDiet(id: string, data: UpdateDietRequest): Promise<{ message: string; diet: Diet }> {
    return apiService.put<{ message: string; diet: Diet }>(`/diets/${id}`, data);
  }

  async deleteDiet(id: string, force?: boolean): Promise<{ message: string }> {
    const suffix = force ? '?force=true' : '';
    const config = force ? { headers: { 'x-force-delete': 'true' } } : undefined;
    return apiService.delete<{ message: string }>(`/diets/${id}${suffix}`, config);
  }

  async bulkUpdateDietStatus(ids: string[], status: 'ACTIVE' | 'INACTIVE'): Promise<{ success: boolean; message: string; count: number }> {
    return apiService.post<{ success: boolean; message: string; count: number }>(`/diets/bulk-status`, { ids, status });
  }


  // Menus
  async getMenus(params?: PaginationParams & { dietId?: string; name?: string; status?: string }): Promise<MenusResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.dietId) queryParams.append('dietId', params.dietId);
    if (params?.name) queryParams.append('name', params.name);
    if (params?.status) queryParams.append('status', params.status);

    return apiService.get<MenusResponse>(`/menus?${queryParams.toString()}`);
  }

  async getMenu(id: string): Promise<{ menu: Menu }> {
    return apiService.get<{ menu: Menu }>(`/menus/${id}`);
  }

  async createMenu(data: CreateMenuRequest): Promise<{ message: string; menu: Menu }> {
    return apiService.post<{ message: string; menu: Menu }>('/menus', data);
  }

  async updateMenu(id: string, data: UpdateMenuRequest): Promise<{ message: string; menu: Menu }> {
    return apiService.put<{ message: string; menu: Menu }>(`/menus/${id}`, data);
  }

  async deleteMenu(id: string, force?: boolean): Promise<{ message: string }> {
    const suffix = force ? '?force=true' : '';
    const config = force ? { headers: { 'x-force-delete': 'true' } } : undefined;
    return apiService.delete<{ message: string }>(`/menus/${id}${suffix}`, config);
  }

  // Menu day plates (schedule)
  async getMenuDayPlates(menuId: string, params: { week: string | number; meal: string }): Promise<{ success: boolean; items: any[] }>{
    const query = new URLSearchParams();
    query.append('week', String(params.week));
    query.append('meal', params.meal);
    return apiService.get<{ success: boolean; items: any[] }>(`/menus/${menuId}/day-plates?${query.toString()}`);
  }

  async getAvailableMenuDayPlates(menuId: string): Promise<{ success: boolean; items: any[] }> {
    return apiService.get<{ success: boolean; items: any[] }>(`/menus/${menuId}/day-plates/available`);
  }

  async saveMenuDayPlates(menuId: string, payload: { week: string | number; meal: string; rows: any[] }): Promise<{ success: boolean; created: number }>{
    return apiService.post<{ success: boolean; created: number }>(`/menus/${menuId}/day-plates/bulk`, payload);
  }


  async bulkUpdateMenuStatus(ids: string[], status: 'ACTIVE' | 'INACTIVE'): Promise<{ success: boolean; message: string; count: number }> {
    return apiService.post<{ success: boolean; message: string; count: number }>(`/menus/bulk-status`, { ids, status });
  }

  // Recipes
  async getRecipes(params?: PaginationParams & { menuId?: string; name?: string; status?: string }): Promise<RecipesResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.menuId) queryParams.append('menuId', params.menuId);
    if (params?.name) queryParams.append('name', params.name);
    if (params?.status) queryParams.append('status', params.status);

    return apiService.get<RecipesResponse>(`/recipes?${queryParams.toString()}`);
  }

  async getRecipe(id: string): Promise<{ recipe: Recipe }> {
    return apiService.get<{ recipe: Recipe }>(`/recipes/${id}`);
  }

  async createRecipe(data: CreateRecipeRequest): Promise<{ message: string; recipe: Recipe }> {
    return apiService.post<{ message: string; recipe: Recipe }>('/recipes', data);
  }

  async updateRecipe(id: string, data: UpdateRecipeRequest): Promise<{ message: string; recipe: Recipe }> {
    return apiService.put<{ message: string; recipe: Recipe }>(`/recipes/${id}`, data);
  }

  async deleteRecipe(id: string): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/recipes/${id}`);
  }


  async bulkUpdateRecipeStatus(ids: string[], status: 'ACTIVE' | 'INACTIVE'): Promise<{ success: boolean; message: string; count: number }> {
    return apiService.post<{ success: boolean; message: string; count: number }>(`/recipes/bulk-status`, { ids, status });
  }

  // Recipe Plates
  async getRecipePlates(params?: PaginationParams & { recipeId?: string; plateSize?: string; name?: string; status?: string; profileId?: string }): Promise<RecipePlatesResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.recipeId) queryParams.append('recipeId', params.recipeId);
    if (params?.plateSize) queryParams.append('plateSize', params.plateSize);
    if (params?.name) queryParams.append('name', params.name);
    if (params?.profileId) queryParams.append('profileId', params.profileId);
    if (params?.status) queryParams.append('status', params.status);

    return apiService.get<RecipePlatesResponse>(`/recipe-plates?${queryParams.toString()}`);
  }

  async getRecipePlate(id: string): Promise<{ recipePlate: RecipePlate }> {
    return apiService.get<{ recipePlate: RecipePlate }>(`/recipe-plates/${id}`);
  }

  async createRecipePlate(data: CreateRecipePlateRequest): Promise<{ message: string; recipePlate: RecipePlate }> {
    return apiService.post<{ message: string; recipePlate: RecipePlate }>('/recipe-plates', data);
  }

  async updateRecipePlate(id: string, data: UpdateRecipePlateRequest): Promise<{ message: string; recipePlate: RecipePlate }> {
    return apiService.put<{ message: string; recipePlate: RecipePlate }>(`/recipe-plates/${id}`, data);
  }

  async deleteRecipePlate(id: string): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/recipe-plates/${id}`);
  }
  // Recipe Plate Profiles
  async getRecipePlateProfiles(params?: { search?: string; dietId?: string; kitchenSectionId?: string; status?: string }): Promise<RecipePlateProfilesResponse> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.dietId) queryParams.append('dietId', params.dietId);
    if (params?.kitchenSectionId) queryParams.append('kitchenSectionId', params.kitchenSectionId);
    if (params?.status) queryParams.append('status', params.status);
    return apiService.get<RecipePlateProfilesResponse>(`/recipe-plate-profiles?${queryParams.toString()}`);
  }

  async createRecipePlateProfile(data: { name: string; dietId?: string; kitchenSectionId?: string; status?: string }): Promise<{ success: boolean; profile: RecipePlateProfile }> {
    return apiService.post<{ success: boolean; profile: RecipePlateProfile }>(`/recipe-plate-profiles`, data);
  }

  async updateRecipePlateProfile(id: string, data: { name?: string; dietId?: string; kitchenSectionId?: string; status?: string }): Promise<{ success: boolean; message: string }> {
    return apiService.put<{ success: boolean; message: string }>(`/recipe-plate-profiles/${id}`, data);
  }

  async getRecipePlateProfileItems(profileId: string): Promise<{ success: boolean; items: RecipePlate[] }> {
    return apiService.get<{ success: boolean; items: RecipePlate[] }>(`/recipe-plate-profiles/${profileId}/items`);
  }

  async addRecipePlateProfileItems(profileId: string, items: Array<Partial<RecipePlate> & { recipeId?: string; ingredientId?: string; quantity?: number; measureUnitId?: string; calories?: number; protein?: number; carbs?: number; fat?: number; status?: string }>): Promise<{ success: boolean; created: number }> {
    return apiService.post<{ success: boolean; created: number }>(`/recipe-plate-profiles/${profileId}/items/bulk`, { items });
  }

  async deleteRecipePlateProfile(profileId: string): Promise<{ success: boolean; message: string }> {
    return apiService.delete<{ success: boolean; message: string }>(`/recipe-plate-profiles/${profileId}`);
  }


  async bulkUpdateRecipePlateStatus(ids: string[], status: 'ACTIVE' | 'INACTIVE'): Promise<{ success: boolean; message: string; count: number }> {
    return apiService.post<{ success: boolean; message: string; count: number }>(`/recipe-plates/bulk-status`, { ids, status });
  }

  // Prices
  async getPrices(params?: PaginationParams & { currency?: string }): Promise<PricesResponse> {
    const queryParams = new URLSearchParams();

    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.sortBy) queryParams.append('sortBy', params.sortBy);
    if (params?.sortOrder) queryParams.append('sortOrder', params.sortOrder);
    if (params?.currency) queryParams.append('currency', params.currency);

    return apiService.get<PricesResponse>(`/prices?${queryParams.toString()}`);
  }

  async getPrice(id: string): Promise<{ price: Price }> {
    return apiService.get<{ price: Price }>(`/prices/${id}`);
  }

  async createPrice(data: CreatePriceRequest): Promise<{ message: string; price: Price }> {
    return apiService.post<{ message: string; price: Price }>('/prices', data);
  }

  async updatePrice(id: string, data: UpdatePriceRequest): Promise<{ message: string; price: Price }> {
    return apiService.put<{ message: string; price: Price }>(`/prices/${id}`, data);
  }

  async deletePrice(id: string): Promise<{ message: string }> {
    return apiService.delete<{ message: string }>(`/prices/${id}`);
  }

  // Export functionality
  async exportDiets(params?: { search?: string; ids?: string[]; name?: string; code?: string; status?: string }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.ids && params.ids.length) queryParams.append('ids', params.ids.join(','));
    if (params?.name) queryParams.append('name', params.name);
    if (params?.code) queryParams.append('code', params.code);
    if (params?.status) queryParams.append('status', params.status);
    const baseURL = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${baseURL}/diets/export?${queryParams.toString()}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }

  async exportMenus(params?: { search?: string; dietId?: string; ids?: string[]; name?: string; status?: string }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.dietId) queryParams.append('dietId', params.dietId);
    if (params?.ids && params.ids.length) queryParams.append('ids', params.ids.join(','));
    if (params?.name) queryParams.append('name', params.name);
    if (params?.status) queryParams.append('status', params.status);
    const baseURL = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${baseURL}/menus/export?${queryParams.toString()}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }

  async exportRecipes(params?: { search?: string; menuId?: string; ids?: string[]; name?: string; status?: string }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.menuId) queryParams.append('menuId', params.menuId);
    if (params?.ids && params.ids.length) queryParams.append('ids', params.ids.join(','));
    if (params?.name) queryParams.append('name', params.name);
    if (params?.status) queryParams.append('status', params.status);
    const baseURL = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${baseURL}/recipes/export?${queryParams.toString()}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }

  async exportRecipePlates(params?: { search?: string; recipeId?: string; name?: string; status?: string; ids?: string[] }): Promise<Blob> {
    const queryParams = new URLSearchParams();
    if (params?.search) queryParams.append('search', params.search);
    if (params?.recipeId) queryParams.append('recipeId', params.recipeId);
    if (params?.name) queryParams.append('name', params.name);
    if (params?.status) queryParams.append('status', params.status);
    if (params?.ids && params.ids.length) queryParams.append('ids', params.ids.join(','));
    const baseURL = process.env.REACT_APP_API_URL || '/api';
    const response = await fetch(`${baseURL}/recipe-plates/export?${queryParams.toString()}`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
    });
    if (!response.ok) throw new Error('Export failed');
    return response.blob();
  }
}

export const dietService = new DietService();
